import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { UserCircleIcon, ArrowRightOnRectangleIcon, CogIcon, UserIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth, usePermissions } from '../hooks/useAuth';
import { UserRole } from '../types/api';

export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { canAdmin } = usePermissions();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'bg-red-100 text-red-800';
      case UserRole.OPERATOR:
        return 'bg-blue-100 text-blue-800';
      case UserRole.VIEWER:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
        <div className="flex items-center space-x-2">
          <UserCircleIcon className="h-8 w-8 text-gray-600" />
          <div className="hidden md:block text-left">
            <div className="text-sm font-medium text-gray-900">{user.username}</div>
            <div className="text-xs text-gray-500">{user.email}</div>
          </div>
          <span className={`hidden md:inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
            {user.role}
          </span>
        </div>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-10 w-10 text-gray-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user.username}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {user.email}
                </div>
                <div className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? 'bg-gray-100' : ''
                  } flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                  onClick={() => {
                    navigate('/profile');
                  }}
                >
                  <UserIcon className="h-4 w-4 mr-3" />
                  Profile
                </button>
              )}
            </Menu.Item>

            {canAdmin() && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-100' : ''
                    } flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                    onClick={() => {
                      navigate('/admin-settings');
                    }}
                  >
                    <CogIcon className="h-4 w-4 mr-3" />
                    Admin Settings
                  </button>
                )}
              </Menu.Item>
            )}

            <div className="border-t border-gray-200"></div>

            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? 'bg-gray-100' : ''
                  } flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                  {isLoggingOut ? 'Signing out...' : 'Sign out'}
                </button>
              )}
            </Menu.Item>
          </div>

          {user.lastLogin && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                Last login: {new Date(user.lastLogin).toLocaleString()}
              </div>
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};